import { useState } from "react";
import { authApi } from "../../services/authApi";
import { useToast } from "../../context/ToastContext";
import { Card } from "../../components/common/Card";
import { Button } from "../../components/common/Button";
import { Badge } from "../../components/common/Badge";

const NOTE_KEY = "beekeeper.travel_mode_note";

/**
 * NOTE: the backend's TravelModeRequest (api/models/schemas.py) only
 * accepts {enabled, regions, expires_at} -- there is no "reason" or
 * "start date" field, and api/functions/auth.py's travel_mode handler
 * never persists one. The "reason" and start-date inputs below are kept
 * client-side only (localStorage) as a personal note; add a
 * `travel_reason`/`starts_at` column to Users.geofencing_travel_mode's
 * JSON shape server-side before treating this as a real audit trail.
 */
export function TravelModeTab() {
  const { notify } = useToast();
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [regions, setRegions] = useState("");
  const [reason, setReason] = useState(localStorage.getItem(NOTE_KEY) || "");
  const [busy, setBusy] = useState(false);

  async function enable() {
    if (!endDate) return notify("End date is required", "warning");
    setBusy(true);
    try {
      await authApi.travelMode({
        enabled: true,
        regions: regions.split(",").map((r) => r.trim()).filter(Boolean),
        expires_at: new Date(endDate).toISOString(),
      });
      localStorage.setItem(NOTE_KEY, reason);
      notify("Travel Mode enabled", "success");
    } catch (err) {
      notify(err.apiMessage || "Could not enable Travel Mode", "danger");
    } finally {
      setBusy(false);
    }
  }

  async function disable() {
    setBusy(true);
    try {
      await authApi.travelMode({ enabled: false, regions: [] });
      notify("Travel Mode disabled", "success");
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card title="Temporary Travel Mode">
      <p>
        <Badge tone="info">Geofencing bypass</Badge> Temporarily allow sign-in from outside your home region.
      </p>
      <div style={{ display: "grid", gap: "var(--space-3)", maxWidth: 420 }}>
        <label>
          Start date
          <input id="travel-mode-tab-start-date" name="start-date" type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="touch-target" style={{ width: "100%" }} />
        </label>
        <label>
          End date
          <input id="travel-mode-tab-end-date" name="end-date" type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className="touch-target" style={{ width: "100%" }} />
        </label>
        <label>
          Regions (comma-separated, optional)
          <input id="travel-mode-tab-regions" name="regions" value={regions} onChange={(e) => setRegions(e.target.value)} className="touch-target" style={{ width: "100%" }} placeholder="e.g. CA, NV" />
        </label>
        <label>
          Reason (personal note only — not synced to your account)
          <textarea id="travel-mode-tab-reason" name="reason" value={reason} onChange={(e) => setReason(e.target.value)} rows={2} style={{ width: "100%" }} />
        </label>
      </div>
      <div style={{ display: "flex", gap: "var(--space-2)", marginTop: "var(--space-4)" }}>
        <Button onClick={enable} disabled={busy}>Enable Travel Mode</Button>
        <Button variant="secondary" onClick={disable} disabled={busy}>Disable</Button>
      </div>
    </Card>
  );
}
